// Extrato do produto (kardex): todas as movimentações com saldo acumulado (unidade e R$) e custo
// médio linha a linha. Cancelados aparecem riscados e não somam.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { FileSpreadsheet } from "lucide-react";
import { getEstoqueKardex, getEstoqueProdutos } from "../../api";
import EstoqueLayout from "./EstoqueLayout";
import { MOTIVOS_AJUSTE, ProdutoSelect, TipoBadge, formatData } from "./comum";
import { AvisoErro, Campo, btnStyle, formatBRL, formatQtd, inputStyle, palette, useIsMobile } from "./ui";

const descricao = (m) =>
  m.documento?.rotulo ||
  [MOTIVOS_AJUSTE.find((x) => x.id === m.motivo_tipo)?.label, m.motivo].filter(Boolean).join(": ") ||
  [m.responsavel_nome, m.destino].filter(Boolean).join(" · ") || "—";

export default function KardexView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const produtoId = params.get("produto") ? Number(params.get("produto")) : null;
  const [produtos, setProdutos] = useState([]);
  const [periodo, setPeriodo] = useState({ inicio: params.get("inicio") || "", fim: params.get("fim") || "" });
  const [kardex, setKardex] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => { getEstoqueProdutos().then(setProdutos).catch((e) => setErro(e.message)); }, []);
  const carregar = useCallback(() => {
    if (!produtoId) { setKardex(null); return; }
    getEstoqueKardex(produtoId, periodo).then(setKardex).catch((e) => setErro(e.message));
  }, [produtoId, periodo]);
  useEffect(() => { carregar(); }, [carregar]);

  const escolherProduto = (id) => {
    const novo = new URLSearchParams(params);
    if (id) novo.set("produto", id); else novo.delete("produto");
    setParams(novo);
  };

  function exportar() {
    const u = kardex.produto.unidade_sigla;
    const linhas = [
      { Data: "", Tipo: "Saldo anterior", Descrição: "", [`Qtd (${u})`]: "", "Valor (R$)": "", [`Saldo (${u})`]: Number(kardex.saldo_anterior.quantidade), "Saldo (R$)": Number(kardex.saldo_anterior.valor), "Custo médio": kardex.saldo_anterior.custo_medio ?? "" },
      ...kardex.movimentos.map((m) => ({
        Data: formatData(m.data), Tipo: m.tipo + (m.status === "CANCELADA" ? " (cancelado)" : ""), Descrição: descricao(m),
        [`Qtd (${u})`]: Number(m.quantidade), "Valor (R$)": Number(m.valor_total),
        [`Saldo (${u})`]: Number(m.saldo_quantidade), "Saldo (R$)": Number(m.saldo_valor), "Custo médio": m.custo_medio ?? "",
      })),
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linhas), "Extrato");
    XLSX.writeFile(wb, `extrato_${kardex.produto.nome.replace(/\W+/g, "_")}.xlsx`);
  }

  const th = (align = "left") => ({ padding: "10px 12px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", textAlign: align, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap", position: "sticky", top: 0, background: C.card });
  const td = (align = "left", extra = {}) => ({ padding: "8px 12px", fontSize: 13, textAlign: align, borderBottom: `1px solid ${C.border}`, whiteSpace: align === "right" ? "nowrap" : "normal", ...extra });

  return (
    <EstoqueLayout titulo="Extrato do Produto" isLight={isLight} setIsLight={setIsLight}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end", marginBottom: 16 }}>
        <div style={{ flex: "1 1 260px" }}><Campo label="Produto" C={C}><ProdutoSelect C={C} produtos={produtos} value={produtoId} onChange={escolherProduto} /></Campo></div>
        <div style={{ flex: "0 1 150px" }}><Campo label="De" C={C}><input type="date" value={periodo.inicio} onChange={(e) => setPeriodo({ ...periodo, inicio: e.target.value })} style={inputStyle(C)} /></Campo></div>
        <div style={{ flex: "0 1 150px" }}><Campo label="Até" C={C}><input type="date" value={periodo.fim} onChange={(e) => setPeriodo({ ...periodo, fim: e.target.value })} style={inputStyle(C)} /></Campo></div>
        {kardex && <button onClick={exportar} style={btnStyle(C.success)}><FileSpreadsheet size={15} /> Excel</button>}
      </div>

      {!produtoId && <div style={{ color: C.muted, fontSize: 13 }}>Escolha um produto para ver o extrato.</div>}
      {kardex && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 12 }}>
            {[
              ["Saldo anterior", kardex.saldo_anterior],
              ["Saldo final", kardex.saldo_final],
            ].map(([rotulo, s]) => (
              <div key={rotulo} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>{rotulo}</div>
                <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2 }}>{formatQtd(s.quantidade)} {kardex.produto.unidade_sigla}</div>
                <div style={{ fontSize: 13, color: C.muted }}>{formatBRL(s.valor)} · custo médio {formatBRL(s.custo_medio)}</div>
              </div>
            ))}
          </div>

          {isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {kardex.movimentos.map((m) => (
                <div key={m.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 10, opacity: m.status === "CANCELADA" ? 0.5 : 1 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <TipoBadge tipo={m.tipo} cancelado={m.status === "CANCELADA"} />
                    <span style={{ fontSize: 12, color: C.muted }}>{formatData(m.data)}</span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700 }}>{formatQtd(m.quantidade)} · {formatBRL(m.valor_total)}</span>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{descricao(m)}</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Saldo: <strong>{formatQtd(m.saldo_quantidade)}</strong> · {formatBRL(m.saldo_valor)} · CM {formatBRL(m.custo_medio)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "auto", maxHeight: "62vh" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th()}>Data</th><th style={th()}>Tipo</th><th style={th()}>Documento / motivo</th>
                    <th style={th("right")}>Qtd.</th><th style={th("right")}>Custo unit.</th><th style={th("right")}>Valor</th>
                    <th style={th("right")}>Saldo qtd.</th><th style={th("right")}>Saldo R$</th><th style={th("right")}>Custo médio</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: C.bgActive }}>
                    <td style={td()} colSpan={6}><strong>Saldo anterior</strong></td>
                    <td style={td("right", { fontWeight: 700 })}>{formatQtd(kardex.saldo_anterior.quantidade)}</td>
                    <td style={td("right", { fontWeight: 700 })}>{formatBRL(kardex.saldo_anterior.valor)}</td>
                    <td style={td("right")}>{formatBRL(kardex.saldo_anterior.custo_medio)}</td>
                  </tr>
                  {kardex.movimentos.map((m) => {
                    const o = m.status === "CANCELADA" ? { opacity: 0.45, textDecoration: "line-through" } : {};
                    return (
                      <tr key={m.id} title={m.status === "CANCELADA" ? `Cancelado: ${m.motivo_cancelamento}` : undefined}>
                        <td style={td("left", { whiteSpace: "nowrap", ...o })}>{formatData(m.data)}</td>
                        <td style={td()}><TipoBadge tipo={m.tipo} cancelado={m.status === "CANCELADA"} /></td>
                        <td style={td("left", { color: C.muted, ...o })}>{descricao(m)}</td>
                        <td style={td("right", { color: Number(m.quantidade) < 0 ? C.danger : C.success, fontWeight: 600, ...o })}>{formatQtd(m.quantidade)}</td>
                        <td style={td("right", o)}>{formatBRL(m.custo_unitario)}</td>
                        <td style={td("right", o)}>{formatBRL(m.valor_total)}</td>
                        <td style={td("right", { fontWeight: 700, color: Number(m.saldo_quantidade) < 0 ? C.danger : C.text })}>{formatQtd(m.saldo_quantidade)}</td>
                        <td style={td("right", { fontWeight: 700 })}>{formatBRL(m.saldo_valor)}</td>
                        <td style={td("right")}>{formatBRL(m.custo_medio)}</td>
                      </tr>
                    );
                  })}
                  {kardex.movimentos.length === 0 && <tr><td colSpan={9} style={td("center", { color: C.muted, padding: 20 })}>Sem movimentações no período.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </EstoqueLayout>
  );
}
